/**
 * Difficulty progression logic
 */

import type { DifficultyConfig } from '../utils/types';
import {
  INITIAL_TIMER_MS,
  TIMER_DECREASE_PER_LEVEL_MS,
  TIMER_SLOW_DECREASE_PER_LEVEL_MS,
  TIMER_THRESHOLD_MS,
  MIN_TIMER_MS,
  INITIAL_STRIKE_THRESHOLD,
  STRIKE_THRESHOLD_DECREASE_INTERVAL,
  STRIKE_THRESHOLD_DECREASE_AMOUNT,
  MIN_STRIKE_THRESHOLD,
} from '../utils/constants';
import { calculatePixelToPointRatio } from './scoring';

/**
 * Calculates timer duration for a given level
 * Two-stage decrease:
 * - Levels 1-13: decrease 200ms/level (3000ms -> 400ms)
 * - Levels 14+: decrease 10ms/level (400ms -> 200ms at level 34)
 */
export function calculateTimerDuration(level: number): number {
  // Calculate when we hit the threshold
  const thresholdLevel = Math.ceil((INITIAL_TIMER_MS - TIMER_THRESHOLD_MS) / TIMER_DECREASE_PER_LEVEL_MS) + 1;

  if (level < thresholdLevel) {
    // Fast decrease phase
    const duration = INITIAL_TIMER_MS - ((level - 1) * TIMER_DECREASE_PER_LEVEL_MS);
    return Math.max(duration, TIMER_THRESHOLD_MS);
  } else {
    // Slow decrease phase
    const levelsAfterThreshold = level - thresholdLevel;
    const duration = TIMER_THRESHOLD_MS - (levelsAfterThreshold * TIMER_SLOW_DECREASE_PER_LEVEL_MS);
    return Math.max(duration, MIN_TIMER_MS);
  }
}

/**
 * Gets the complete difficulty configuration for a level
 */
export function getDifficultyConfig(level: number): DifficultyConfig {
  return {
    timerDuration: calculateTimerDuration(level),
    pixelToPointRatio: calculatePixelToPointRatio(level),
    strikeThreshold: calculateStrikeThreshold(level),
  };
}

/**
 * Determines if a level is considered "high difficulty"
 * High difficulty starts when timer reaches minimum
 */
export function isHighDifficulty(level: number): boolean {
  return calculateTimerDuration(level) === MIN_TIMER_MS;
}

/**
 * Calculates what level the timer reaches minimum duration
 */
export function getMinTimerLevel(): number {
  // (INITIAL_TIMER_MS - MIN_TIMER_MS) / TIMER_DECREASE_PER_LEVEL_MS + 1
  return Math.floor((INITIAL_TIMER_MS - MIN_TIMER_MS) / TIMER_DECREASE_PER_LEVEL_MS) + 1;
}

/**
 * Calculates strike distance threshold for a given level
 * Decreases by 5px every 5 levels: 50px (1-4) -> 45px (5-9) -> 40px (10-14) -> ... -> 25px min (25+)
 */
export function calculateStrikeThreshold(level: number): number {
  const decreaseCount = Math.floor(level / STRIKE_THRESHOLD_DECREASE_INTERVAL);
  const threshold = INITIAL_STRIKE_THRESHOLD - (decreaseCount * STRIKE_THRESHOLD_DECREASE_AMOUNT);
  return Math.max(threshold, MIN_STRIKE_THRESHOLD);
}
